import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, PawPrint } from "lucide-react";


const TIER_OPTIONS = [
  {
    id: "Steadfast",
    label: "The Steadfast",
    price: "S$680/yr",
    desc: "Annual wellness, core vaccinations, genomics baseline",
  },
  {
    id: "Devoted",
    label: "The Devoted",
    price: "S$1,180/yr",
    desc: "Bi-annual exams, bloodwork, advanced genomics, urgent care",
    popular: true,
  },
  {
    id: "Besotted",
    label: "The Besotted",
    price: "S$2,980/yr",
    desc: "Full workup, 24/7 care line, dental, specialist access",
  },
];

const REFERRAL_OPTIONS = [
  "Instagram / Social media",
  "A friend or family member",
  "Google search",
  "Editorial / Press",
  "A Furbaby event",
  "Other",
];

const SPECIES_OPTIONS = [
  "Dog", "Cat", "Rabbit", "Bird", "Hamster", "Guinea pig", "Other",
];

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
  initialTier?: string;
}

type Step = 1 | 2 | 3 | "success";

export default function WaitlistModal({ open, onClose, initialTier }: WaitlistModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    petName: "",
    species: "",
    breed: "",
    tierInterest: initialTier ?? "",
    referralSource: "",
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      setError(null);
      if (initialTier) setForm(f => ({ ...f, tierInterest: initialTier }));
      fetch("/api/waitlist/count")
        .then(r => r.json())
        .then(d => setCount(d.count))
        .catch(() => {});
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, initialTier]);

  const patch = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const canNext1 = form.firstName.trim() && form.email.trim().includes("@");
  const canNext2 = form.petName.trim() && form.species;

  async function submit() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          petName: form.petName,
          species: form.species,
          breed: form.breed,
          tierInterest: form.tierInterest,
          referralSource: form.referralSource,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        if (data.error?.includes("duplicate") || data.error?.includes("already")) {
          setStep("success");
          return;
        }
        throw new Error(data.error ?? "Something went wrong");
      }
      const data = await res.json();
      setCount(data.count ?? count);
      setStep("success");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const inputCls = "w-full bg-transparent border-b border-foreground/20 py-3 font-sans text-base focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-25";
  const labelCls = "font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-1 block";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[200] bg-foreground/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-[201] flex items-end md:items-center justify-center p-0 md:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative w-full max-w-xl bg-background rounded-t-[2rem] md:rounded-[2rem] overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center justify-between px-6 md:px-8 pt-6 pb-4 border-b border-border/30 flex-shrink-0">
                <div>
                  <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-0.5">
                    Founding Member Waitlist
                  </p>
                  {count !== null && step !== "success" && (
                    <p className="font-sans text-xs opacity-50">
                      Join {count.toLocaleString()} founding members already on the list
                    </p>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors duration-200"
                >
                  <X className="w-4 h-4 opacity-50" />
                </button>
              </div>

              {step !== "success" && (
                <div className="flex gap-1 px-6 md:px-8 pt-4 flex-shrink-0">
                  {([1, 2, 3] as const).map(s => (
                    <div
                      key={s}
                      className={`flex-1 h-0.5 rounded-full transition-colors duration-500 ${step >= s ? "bg-primary" : "bg-border"}`}
                    />
                  ))}
                </div>
              )}

              <div className="overflow-y-auto flex-1 px-6 md:px-8 py-6">
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <h2 className="font-serif text-3xl mb-6">About you</h2>
                      <div className="flex flex-col gap-5">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={labelCls}>First name *</label>
                            <input
                              type="text" value={form.firstName} onChange={patch("firstName")}
                              placeholder="Your name" className={inputCls} autoFocus
                            />
                          </div>
                          <div>
                            <label className={labelCls}>Last name</label>
                            <input
                              type="text" value={form.lastName} onChange={patch("lastName")}
                              placeholder="Optional" className={inputCls}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={labelCls}>Email address *</label>
                          <input
                            type="email" value={form.email} onChange={patch("email")}
                            placeholder="you@example.com" className={inputCls}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <h2 className="font-serif text-3xl mb-6">About your furchild</h2>
                      <div className="flex flex-col gap-5">
                        <div>
                          <label className={labelCls}>Pet name *</label>
                          <input
                            type="text" value={form.petName} onChange={patch("petName")}
                            placeholder="e.g. Biscuit" className={inputCls} autoFocus
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={labelCls}>Species *</label>
                            <select value={form.species} onChange={patch("species")} className={inputCls}>
                              <option value="">Select…</option>
                              {SPECIES_OPTIONS.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={labelCls}>Breed</label>
                            <input
                              type="text" value={form.breed} onChange={patch("breed")}
                              placeholder="e.g. Shiba Inu" className={inputCls}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <h2 className="font-serif text-3xl mb-2">Membership interest</h2>
                      <p className="font-sans text-sm opacity-50 mb-6">
                        Founding members lock in the accelerated loyalty schedule — 10%/yr, reaching 90% discount by Year 9.
                      </p>
                      <div className="flex flex-col gap-3 mb-6">
                        {TIER_OPTIONS.map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, tierInterest: t.id }))}
                            className={`text-left p-4 rounded-xl border transition-all duration-300 ${
                              form.tierInterest === t.id
                                ? "border-primary bg-primary/8"
                                : "border-border/40 hover:border-primary/40"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="font-serif text-lg">{t.label}</span>
                              <div className="flex items-center gap-2">
                                {t.popular && (
                                  <span className="font-grotesk text-[8px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                                    Popular
                                  </span>
                                )}
                                <span className="font-grotesk text-xs opacity-60">{t.price}</span>
                              </div>
                            </div>
                            <p className="font-sans text-xs opacity-50">{t.desc}</p>
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className={labelCls}>How did you hear about us?</label>
                        <select value={form.referralSource} onChange={patch("referralSource")} className={inputCls}>
                          <option value="">Select…</option>
                          {REFERRAL_OPTIONS.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                      {error && (
                        <p className="font-sans text-sm text-destructive mt-4">{error}</p>
                      )}
                    </motion.div>
                  )}

                  {step === "success" && (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      className="text-center py-8"
                    >
                      <motion.div
                        className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.1, type: "spring", stiffness: 200 }}
                      >
                        <PawPrint className="w-8 h-8 text-primary-foreground" strokeWidth={1.5} />
                      </motion.div>
                      <h2 className="font-serif text-4xl mb-3">You're on the list.</h2>
                      {count !== null && (
                        <p className="font-grotesk text-xs uppercase tracking-widest text-primary mb-4">
                          Member #{count.toLocaleString()} of 2,000
                        </p>
                      )}
                      <p className="font-sans text-sm opacity-60 max-w-xs mx-auto leading-relaxed mb-6">
                        A confirmation is on its way to <strong className="opacity-90">{form.email}</strong>.
                        We'll reach out when founding membership spots open — and you'll be first in line.
                      </p>
                      <div className="p-4 rounded-xl bg-primary/8 border border-primary/20 text-left mb-6">
                        <p className="font-grotesk text-[10px] uppercase tracking-widest text-primary mb-2">Founding member schedule</p>
                        <p className="font-sans text-xs opacity-70 leading-relaxed">
                          10% loyalty discount per year · reaches 90% by Year 9 · locked for life from your join date
                        </p>
                      </div>
                      <button
                        onClick={onClose}
                        className="font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-300"
                      >
                        Close
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {step !== "success" && (
                <div className="px-6 md:px-8 pb-6 pt-4 border-t border-border/30 flex items-center justify-between flex-shrink-0">
                  {step > 1 ? (
                    <button
                      onClick={() => setStep(s => (s as number - 1) as Step)}
                      className="font-grotesk text-xs uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity duration-300"
                    >
                      Back
                    </button>
                  ) : <div />}

                  {step < 3 ? (
                    <button
                      onClick={() => step === 1 && canNext1 ? setStep(2) : step === 2 && canNext2 ? setStep(3) : null}
                      disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
                      className="group inline-flex items-center gap-2 font-grotesk text-sm uppercase tracking-widest bg-primary text-primary-foreground px-6 py-3 rounded-full hover:opacity-85 transition-opacity duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Continue
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={loading}
                      className="group inline-flex items-center gap-2 font-grotesk text-sm uppercase tracking-widest bg-primary text-primary-foreground px-6 py-3 rounded-full hover:opacity-85 transition-opacity duration-300 disabled:opacity-50"
                    >
                      {loading ? "Joining…" : "Join the Waitlist"}
                      {!loading && <Check className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
